import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportComponent, ComponentDefinition, COMPONENT_DEFINITIONS } from '@/pages/reports/schemas';
import { reportsService } from '@/services/reports';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { useToast } from '@/hooks/use-toast';
import { validateComponentProps } from '../utils/validateComponentProps';
import { DEFAULT_REPORT_LANG, PARTNER_NONE } from '@/pages/reports/new/constants';
import { normalizeReportLang, type ReportLocale } from '@/pages/reports/i18n';
import { normalizeHeroFirst } from '@/pages/reports/utils/heroOrder';
import { componentPropsToSave } from '@/pages/reports/utils/componentPropsToSave';
import { validateReportFinalize } from '@/pages/reports/utils/validateReportFinalize';
import { REPORT_STATUS_AWAITING_SEND } from '@/lib/reportStatus';
import { trackReportUpdated } from '@/lib/reportUpdated';
import {
  loadThematicSessionTemplates,
  createThematicSessionTemplate,
  updateThematicSessionTemplate,
  deleteThematicSessionTemplate,
  type ThematicSessionTemplate,
} from '@/pages/reports/utils/thematicSessionTemplates';

export type ReportNewSection = 'basic' | 'components';

const AUTO_SAVE_DEBOUNCE_MS = 1000;

export function useReportNew() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { partners } = useData();
  const [title, setTitle] = useState('');
  const [edition, setEdition] = useState('');
  const [components, setComponents] = useState<ReportComponent[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<ComponentDefinition | null>(null);
  const [componentProps, setComponentProps] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [titleError, setTitleError] = useState('');
  const [editionError, setEditionError] = useState('');
  const [partnerError, setPartnerError] = useState('');
  const [langError, setLangError] = useState('');
  const [componentsError, setComponentsError] = useState('');
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingBasicInfo, setIsSavingBasicInfo] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [lang, setLang] = useState<ReportLocale>(DEFAULT_REPORT_LANG);
  const [createdReportId, setCreatedReportId] = useState<string | null>(null);
  const [hasSavedBasicInfo, setHasSavedBasicInfo] = useState(false);
  const [openSection, setOpenSection] = useState<ReportNewSection>('basic');
  const [thematicTemplates, setThematicTemplates] = useState<ThematicSessionTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistInFlightRef = useRef(false);
  const skipNextAutoSaveRef = useRef(false);
  const persistDraftRef = useRef<(() => Promise<boolean>) | null>(null);
  const partnerOptions = useMemo(
    () => partners.map((p) => ({ value: p.id, label: p.name })),
    [partners]
  );

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await loadThematicSessionTemplates();
        setThematicTemplates(templates);
      } catch {
        toast({
          variant: 'error',
          title: 'Erro ao carregar modelos',
          description: 'Nao foi possivel carregar os modelos salvos.',
        });
      }
    };
    loadTemplates();
  }, [toast]);

  const handleAddComponent = useCallback((componentDef: ComponentDefinition) => {
    if (!hasSavedBasicInfo) return;
    if (
      componentDef.type === 'hero' &&
      components.some((component) => component.type === 'hero')
    ) {
      return;
    }
    setSelectedComponent(componentDef);
    setComponentProps({});
    setEditingComponentId(null);
    setSelectedTemplateId('');
    if (componentDef.type === 'hero') {
      setComponentsError('');
    }
  }, [components, hasSavedBasicInfo]);

  const handleEditComponent = useCallback((component: ReportComponent) => {
    const componentDef = COMPONENT_DEFINITIONS.find((c) => c.type === component.type);
    if (componentDef) {
      setSelectedComponent(componentDef);
      setComponentProps(component.props);
      setEditingComponentId(component.id);
      setSelectedTemplateId('');
    }
  }, []);

  const saveCurrentAsTemplate = useCallback(async (name: string) => {
    if (selectedComponent?.type !== 'thematic-session') return;
    if (!name.trim()) return;
    try {
      const createdTemplate = await createThematicSessionTemplate({
        name: name.trim(),
        metadata: { ...componentProps },
      });
      setThematicTemplates((prev) => [
        createdTemplate,
        ...prev.filter((template) => template.id !== createdTemplate.id),
      ]);
      setSelectedTemplateId(createdTemplate.id);
      toast({
        title: 'Modelo salvo',
        description: 'Modelo criado com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao salvar modelo',
        description: 'Nao foi possivel salvar o modelo.',
      });
    }
  }, [componentProps, selectedComponent?.type, toast]);

  const updateSelectedTemplate = useCallback(async (name: string) => {
    if (selectedComponent?.type !== 'thematic-session' || !selectedTemplateId) return;
    if (!name.trim()) return;
    try {
      const updatedTemplate = await updateThematicSessionTemplate(selectedTemplateId, {
        name: name.trim(),
        metadata: { ...componentProps },
      });
      setThematicTemplates((prev) =>
        prev.map((template) => (template.id === selectedTemplateId ? updatedTemplate : template))
      );
      toast({
        title: 'Modelo atualizado',
        description: 'Modelo atualizado com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao atualizar modelo',
        description: 'Nao foi possivel atualizar o modelo.',
      });
    }
  }, [componentProps, selectedComponent?.type, selectedTemplateId, toast]);

  const deleteSelectedTemplate = useCallback(async () => {
    if (selectedComponent?.type !== 'thematic-session' || !selectedTemplateId) return;
    try {
      await deleteThematicSessionTemplate(selectedTemplateId);
      setThematicTemplates((prev) => prev.filter((template) => template.id !== selectedTemplateId));
      setSelectedTemplateId('');
      toast({
        title: 'Modelo excluido',
        description: 'Modelo removido com sucesso.',
      });
    } catch {
      toast({
        variant: 'error',
        title: 'Erro ao excluir modelo',
        description: 'Nao foi possivel excluir o modelo.',
      });
    }
  }, [selectedComponent?.type, selectedTemplateId, toast]);

  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const found = thematicTemplates.find((template) => template.id === templateId);
    if (!found) return;
    setComponentProps(found.props);
    setValidationErrors({});
  }, [thematicTemplates]);

  const clearTemplateForm = useCallback(() => {
    setSelectedTemplateId('');
    setComponentProps({});
    setValidationErrors({});
  }, []);

  const handleSaveComponent = useCallback(() => {
    if (!selectedComponent) return;

    const errors = validateComponentProps(componentProps, selectedComponent);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const propsToSave = componentPropsToSave(selectedComponent, componentProps);

    if (editingComponentId) {
      setComponents((prev) => {
        const updated = prev.map((comp) =>
          comp.id === editingComponentId ? { ...comp, props: propsToSave } : comp
        );
        return normalizeHeroFirst(updated);
      });
      setEditingComponentId(null);
      setSelectedComponent(null);
    } else {
      if (
        selectedComponent.type === 'hero' &&
        components.some((component) => component.type === 'hero')
      ) {
        setSelectedComponent(null);
        setComponentProps({});
        setValidationErrors({});
        return;
      }
      const newComponent: ReportComponent = {
        id: crypto.randomUUID(),
        type: selectedComponent.type,
        props: propsToSave,
      };
      setComponents((prev) => normalizeHeroFirst([...prev, newComponent]));
      setSelectedComponent(null);
    }
    setComponentProps({});
    setValidationErrors({});
  }, [selectedComponent, componentProps, editingComponentId, components]);

  const handleRemoveComponent = useCallback((componentId: string) => {
    setComponents((prev) => normalizeHeroFirst(prev.filter((c) => c.id !== componentId)));
  }, []);

  const handleMoveComponent = useCallback((componentId: string, direction: 'up' | 'down') => {
    setComponents((prev) => {
      const currentIndex = prev.findIndex((c) => c.id === componentId);
      if (currentIndex === -1) return prev;

      const current = prev[currentIndex];
      if (current.type === 'hero') return prev;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      if (newIndex === 0) return prev;
      if (prev[newIndex].type === 'hero') return prev;

      const newComponents = [...prev];
      const [movedComponent] = newComponents.splice(currentIndex, 1);
      newComponents.splice(newIndex, 0, movedComponent);
      return normalizeHeroFirst(newComponents);
    });
  }, []);

  const buildContentForSave = useCallback((): ReportComponent[] => {
    let list = components.filter((c) => c.type !== 'footer');
    if (editingComponentId && selectedComponent) {
      const propsToSave = componentPropsToSave(selectedComponent, componentProps);
      list = list.map((comp) =>
        comp.id === editingComponentId ? { ...comp, props: propsToSave } : comp
      );
    }
    return normalizeHeroFirst(list);
  }, [components, editingComponentId, selectedComponent, componentProps]);

  const validateBasicInfo = useCallback(() => {
    const nextTitleError = title.trim() ? '' : 'Título é obrigatório';
    const nextEditionError = edition.trim() ? '' : 'Número da edição é obrigatório';
    const nextPartnerError =
      partnerId && partnerId !== PARTNER_NONE ? '' : 'Marca é obrigatória';
    const nextLangError = lang === 'pt' || lang === 'es' ? '' : 'Idioma é obrigatório';

    setTitleError(nextTitleError);
    setEditionError(nextEditionError);
    setPartnerError(nextPartnerError);
    setLangError(nextLangError);

    return !nextTitleError && !nextEditionError && !nextPartnerError && !nextLangError;
  }, [title, edition, partnerId, lang]);

  const scrollToSection = useCallback((target: ReportNewSection) => {
    const element = document.getElementById(
      target === 'basic' ? 'report-basic-section' : 'report-components-section'
    );
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const persistDraft = useCallback(async () => {
    if (!createdReportId || !hasSavedBasicInfo) return false;
    if (persistInFlightRef.current) return false;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    persistInFlightRef.current = true;
    setAutoSaveStatus('saving');
    try {
      await reportsService.updateReport(createdReportId, {
        title: title.trim(),
        edition: edition.trim(),
        content: buildContentForSave(),
        partner_id: partnerId,
        lang: normalizeReportLang(lang),
        trigger_source: 'autosave',
      });
      setLastSavedAt(new Date());
      setAutoSaveStatus('saved');
      return true;
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      setAutoSaveStatus('error');
      return false;
    } finally {
      persistInFlightRef.current = false;
    }
  }, [
    createdReportId,
    hasSavedBasicInfo,
    title,
    edition,
    partnerId,
    lang,
    buildContentForSave,
  ]);

  persistDraftRef.current = persistDraft;

  const handleSaveBasicInfo = useCallback(async () => {
    if (!validateBasicInfo()) {
      setOpenSection('basic');
      return;
    }

    try {
      setIsSavingBasicInfo(true);
      const payload = {
        title: title.trim(),
        edition: edition.trim(),
        content: createdReportId ? buildContentForSave() : [],
        partner_id: partnerId,
        lang: normalizeReportLang(lang),
        trigger_source: 'save_button' as const,
      };

      if (createdReportId) {
        await reportsService.updateReport(createdReportId, payload);
        trackReportUpdated(createdReportId, 'save_button');
      } else {
        const report = await reportsService.createReport(payload, user?.email);
        setCreatedReportId(report.id);
      }

      skipNextAutoSaveRef.current = true;
      setHasSavedBasicInfo(true);
      setOpenSection('components');
      toast({
        variant: 'success',
        title: 'Conteúdo salvo com sucesso',
      });
    } catch (error) {
      console.error('Erro ao salvar dados do report:', error);
      setOpenSection('basic');
      toast({
        variant: 'error',
        title: 'Erro ao salvar',
        description: 'Tente novamente ou entre em contato com o suporte.',
      });
    } finally {
      setIsSavingBasicInfo(false);
    }
  }, [
    validateBasicInfo,
    title,
    edition,
    partnerId,
    lang,
    createdReportId,
    buildContentForSave,
    user?.email,
    toast,
  ]);

  useEffect(() => {
    if (!createdReportId || !hasSavedBasicInfo) return;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    setAutoSaveStatus((current) => (current === 'saving' ? current : 'idle'));

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      persistDraftRef.current?.();
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [
    createdReportId,
    hasSavedBasicInfo,
    title,
    edition,
    partnerId,
    lang,
    components,
    componentProps,
    editingComponentId,
    selectedComponent,
  ]);

  const finalizeReport = useCallback(
    async (mode: 'close' | 'send') => {
      if (!createdReportId) {
        setOpenSection('basic');
        return;
      }

      const validation = validateReportFinalize({
        title,
        edition,
        partnerId,
        lang,
        components: buildContentForSave(),
      });

      setTitleError(validation.titleError);
      setEditionError(validation.editionError);
      setPartnerError(validation.partnerError);
      setLangError(validation.langError);
      setComponentsError(validation.componentsError);

      if (!validation.ok) {
        if (validation.target) {
          setOpenSection(validation.target);
          scrollToSection(validation.target);
        }
        return;
      }

      try {
        setIsSaving(true);
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }

        await reportsService.updateReport(createdReportId, {
          title: title.trim(),
          edition: edition.trim(),
          content: buildContentForSave(),
          partner_id: partnerId,
          lang: normalizeReportLang(lang),
          status: REPORT_STATUS_AWAITING_SEND,
          trigger_source: 'save_button',
        });
        trackReportUpdated(createdReportId, 'save_button');

        toast({
          variant: 'success',
          title: 'Conteúdo salvo com sucesso',
        });

        if (mode === 'send') {
          try {
            await reportsService.update(createdReportId, {
              status: REPORT_STATUS_AWAITING_SEND,
            });
          } catch {
            toast({
              variant: 'error',
              title: 'Não foi possível preparar o envio',
              description:
                'O report ainda está em rascunho. Tente finalizar novamente.',
            });
            return;
          }
          navigate(`/app/reports/${createdReportId}/view/send`);
          return;
        }

        navigate('/app/reports');
      } catch (error) {
        console.error('Erro ao finalizar report:', error);
        toast({
          variant: 'error',
          title: 'Erro ao finalizar',
          description: 'Tente novamente ou entre em contato com o suporte.',
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      createdReportId,
      title,
      edition,
      partnerId,
      lang,
      buildContentForSave,
      scrollToSection,
      navigate,
      toast,
    ]
  );

  const handleFinalizeClose = useCallback(() => {
    return finalizeReport('close');
  }, [finalizeReport]);

  const handleFinalizeSend = useCallback(() => {
    return finalizeReport('send');
  }, [finalizeReport]);

  const handleTitleChange = useCallback((value: string) => {
    setTitle(value);
    setTitleError('');
  }, []);

  const handleEditionChange = useCallback((value: string) => {
    setEdition(value);
    setEditionError('');
  }, []);

  const handlePartnerChange = useCallback((value: string) => {
    setPartnerId(value);
    setPartnerError('');
  }, []);

  const handleLangChange = useCallback((value: string) => {
    setLang(normalizeReportLang(value));
    setLangError('');
  }, []);

  const handlePropChange = useCallback((key: string, value: string | any[]) => {
    setComponentProps((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setSelectedComponent(null);
    setEditingComponentId(null);
    setSelectedTemplateId('');
  }, []);

  const handleSectionOpenChange = useCallback(
    (section: ReportNewSection, open: boolean) => {
      if (section === 'components' && !hasSavedBasicInfo) {
        return;
      }
      if (!open) {
        if (section === 'basic' && !hasSavedBasicInfo) {
          return;
        }
        setOpenSection(section === 'basic' ? 'components' : 'basic');
        return;
      }
      setOpenSection(section);
    },
    [hasSavedBasicInfo]
  );

  return {
    title,
    titleError,
    edition,
    editionError,
    onEditionChange: handleEditionChange,
    components,
    selectedComponent,
    componentProps,
    validationErrors,
    editingComponentId,
    isSaving,
    isSavingBasicInfo,
    partnerId,
    partnerError,
    lang,
    langError,
    componentsError,
    partnerOptions,
    handleTitleChange,
    handleAddComponent,
    handleEditComponent,
    handleSaveComponent,
    handleRemoveComponent,
    handleMoveComponent,
    handlePropChange,
    handleSaveBasicInfo,
    handleFinalizeClose,
    handleFinalizeSend,
    cancelEdit,
    hasSavedBasicInfo,
    openSection,
    handleSectionOpenChange,
    onPartnerChange: handlePartnerChange,
    onLangChange: handleLangChange,
    thematicTemplates,
    selectedTemplateId,
    onTemplateSelect: handleTemplateSelect,
    onClearAll: clearTemplateForm,
    onSaveTemplate: saveCurrentAsTemplate,
    onUpdateTemplate: updateSelectedTemplate,
    onDeleteTemplate: deleteSelectedTemplate,
    autoSaveStatus,
    lastSavedAt,
  };
}
